/**
 * Hook customizado para busca de músicas
 * Centraliza toda a lógica de busca para evitar duplicação
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { searchSongs, getAllSongs } from '@/services/songs';
import { Song } from '@/types/db';
import { removeAccents, normalizeForSearch } from '@/utils/stringUtils';
import { isBiblicalBookSearch, BIBLICAL_BOOKS_SEARCH_VARIANTS } from '@/constants/searchConstants';

interface UseSongSearchOptions {
  debounceMs?: number;
  onResultsChange?: (results: Song[]) => void;
}

interface UseSongSearchReturn {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  results: Song[];
  loading: boolean;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  performSearch: (term: string) => Promise<Song[]>;
  forceSearch: (term: string) => Promise<void>;
  clearSearch: () => void;
}

/**
 * Hook para busca de músicas com debounce e cache
 */
export function useSongSearch(options: UseSongSearchOptions = {}): UseSongSearchReturn {
  const { debounceMs = 300, onResultsChange } = options;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Song[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchIdRef = useRef(0);

  /**
   * Filtra músicas baseado no termo de busca
   */
  const filterSongs = useCallback((songs: Song[], normalizedTerm: string): Song[] => {
    // Verifica se é busca por salmos (usa o termo original normalizado)
    const isSalmo = normalizedTerm === 'salmo' || normalizedTerm === 'salmos';
    const isStrictBiblicalBook = BIBLICAL_BOOKS_SEARCH_VARIANTS.some(book => removeAccents(book) === normalizedTerm);

    // Primeiro, busca correspondência exata na descrição
    const exactMatch = songs.find(song => {
      const desc = normalizeForSearch(song.descricao || '');
      return desc === normalizedTerm;
    });

    if (exactMatch) {
      return [exactMatch];
    }

    // Filtro local insensível a acentos
    const filtered = songs.filter(song => {
      const desc = normalizeForSearch(song.descricao || '');
      const livro = normalizeForSearch(song.livro || '');
      const vers = normalizeForSearch(song.versiculos || '');

      if (isSalmo) {
        // Busca por salmos: retorna músicas onde o livro contém "salmo"
        return livro.includes('salmo');
      }

      if (isStrictBiblicalBook) {
        // Busca por livro bíblico específico
        return livro.includes(normalizedTerm);
      }

      // Busca geral
      return desc.includes(normalizedTerm) || livro.includes(normalizedTerm) || vers.includes(normalizedTerm);
    });

    // Ordena: músicas grátis primeiro
    return filtered.sort((a, b) => {
      const aIsFree = a.is_free !== false;
      const bIsFree = b.is_free !== false;
      if (aIsFree === bIsFree) return 0;
      return aIsFree ? -1 : 1;
    });
  }, []);

  /**
   * Executa a busca de músicas
   */
  const performSearch = useCallback(async (term: string): Promise<Song[]> => {
    if (!term.trim()) {
      return [];
    }

    const currentSearchId = ++searchIdRef.current;
    const normalizedTerm = normalizeForSearch(term);
    const isBiblicalBook = isBiblicalBookSearch(normalizedTerm);
    
    let songs: Song[] = [];
    
    try {
      // Se for livro bíblico ou termo longo, busca todas as músicas
      if (isBiblicalBook || term.length > 3) {
        songs = await getAllSongs();
      } else {
        songs = await searchSongs(term);
      }

      const filtered = filterSongs(songs, normalizedTerm);

      // Só retorna se for a busca mais recente
      if (searchIdRef.current === currentSearchId) {
        return filtered;
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao buscar músicas:', error);
      if (searchIdRef.current === currentSearchId) {
        return [];
      }
      return [];
    }
  }, [filterSongs]);

  /**
   * Força uma busca imediata e atualiza o estado (usado quando Enter é pressionado)
   */
  const forceSearch = useCallback(async (term: string): Promise<void> => {
    if (!term.trim()) {
      setResults([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }

    const currentSearchId = ++searchIdRef.current;
    setLoading(true);
    setShowDropdown(true);

    try {
      const normalizedTerm = normalizeForSearch(term);
      const isBiblicalBook = isBiblicalBookSearch(normalizedTerm);
      
      let songs: Song[] = [];
      
      // Se for livro bíblico ou termo longo, busca todas as músicas
      if (isBiblicalBook || term.length > 3) {
        songs = await getAllSongs();
      } else {
        songs = await searchSongs(term);
      }

      const filtered = filterSongs(songs, normalizedTerm);
      
      // Atualiza resultados se for a busca mais recente
      if (searchIdRef.current === currentSearchId) {
        setResults(filtered);
        setShowDropdown(true);
        setLoading(false);
        onResultsChange?.(filtered);
      }
    } catch (error) {
      console.error('Erro ao forçar busca:', error);
      if (searchIdRef.current === currentSearchId) {
        setResults([]);
        setShowDropdown(false);
        setLoading(false);
      }
    }
  }, [filterSongs, onResultsChange]);

  /**
   * Limpa a busca
   */
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    setLoading(false);
  }, []);

  // Busca automática com debounce quando searchTerm muda
  useEffect(() => {
    if (!searchTerm.trim()) {
      clearSearch();
      return;
    }

    const currentSearchId = ++searchIdRef.current;
    setLoading(true);
    setShowDropdown(true); // Mostra dropdown imediatamente quando começa a digitar

    const handler = setTimeout(async () => {
      try {
        const normalizedTerm = normalizeForSearch(searchTerm);
        const isBiblicalBook = isBiblicalBookSearch(normalizedTerm);
        
        let songs: Song[] = [];
        
        // Se for livro bíblico ou termo longo, busca todas as músicas
        if (isBiblicalBook || searchTerm.length > 3) {
          songs = await getAllSongs();
        } else {
          songs = await searchSongs(searchTerm);
        }

        const filtered = filterSongs(songs, normalizedTerm);
        
        // Só atualiza se for a busca mais recente
        if (searchIdRef.current === currentSearchId) {
          setResults(filtered);
          setShowDropdown(true); // Garante que o dropdown está visível
          setLoading(false);
          onResultsChange?.(filtered);
        }
      } catch (error) {
        console.error('Erro na busca automática:', error);
        // Limpa resultados em caso de erro
        if (searchIdRef.current === currentSearchId) {
          setResults([]);
          setShowDropdown(false);
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => clearTimeout(handler);
  }, [searchTerm, filterSongs, clearSearch, onResultsChange, debounceMs]);

  return {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    showDropdown,
    setShowDropdown,
    performSearch,
    forceSearch,
    clearSearch
  };
}

